/**
 * useDraggablePanel — make any floating popup draggable by a header handle.
 *
 * Shared across editors so every popup gets the same behaviour. Spread
 * `headerProps` onto the element you want to grab (the title bar) and merge
 * `draggedStyle` into the panel's style AFTER its own styles so it can override
 * the panel's positioning once you start dragging.
 *
 * On the first grab it snapshots the panel's current on-screen rect and switches
 * to fixed pixel positioning there — so there's no jump regardless of how the
 * panel was originally centred (translateX(-50%), right-anchored, etc.). Drags
 * that begin on an interactive control (button/input/slider, or anything tagged
 * data-no-drag) are ignored so the panel's controls keep working.
 */

import React, { useCallback, useRef, useState } from 'react';

interface Pos { x: number; y: number; }

export function useDraggablePanel() {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Pos | null>(null); // null → use the panel's own CSS layout

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, select, a, [data-no-drag]')) return;
    const panel = panelRef.current;
    if (!panel) return;
    e.preventDefault();

    // Snapshot the current position so dragging starts exactly where it sits.
    const rect = panel.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY;
    const baseX = rect.left, baseY = rect.top;
    const w = rect.width, h = rect.height;

    const move = (ev: PointerEvent) => {
      const margin = 24; // keep at least this much of the panel on-screen
      const nx = Math.max(margin - w, Math.min(window.innerWidth - margin, baseX + (ev.clientX - startX)));
      const ny = Math.max(0, Math.min(window.innerHeight - margin, baseY + (ev.clientY - startY)));
      setPos({ x: nx, y: ny });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  const headerProps = { onPointerDown, style: { cursor: 'move', touchAction: 'none' } as React.CSSProperties };

  // Once dragged, pin the panel to absolute pixels and drop any centring transform.
  const draggedStyle: React.CSSProperties = pos
    ? { position: 'fixed', left: pos.x, top: pos.y, right: 'auto', bottom: 'auto', transform: 'none', margin: 0 }
    : {};

  return { panelRef, headerProps, draggedStyle };
}
